"use client";

import { useLayoutEffect, useRef, useState } from "react";
import type { ViewState } from "react-map-gl";

export function staticMapImageUrl(
  style: string,
  token: string,
  view: Partial<ViewState>,
  width: number,
  height: number
) {
  const match = /^mapbox:\/\/styles\/([\w-]+)\/([\w-]+)$/.exec(style);
  if (!match) throw new Error("This map style requires GL capture.");
  const { longitude = 0, latitude = 0, zoom = 0, bearing = 0, pitch = 0 } = view;
  if (![longitude, latitude, zoom, bearing, pitch, width, height].every(Number.isFinite) || width <= 0 || height <= 0)
    throw new Error("Map geometry must be finite and non-empty.");
  const ratio = Math.min(1, 1280 / width, 1280 / height);
  const camera = [
    longitude,
    latitude,
    Math.max(0, Math.min(22, zoom + Math.log2(ratio))),
    bearing,
    Math.min(60, Math.max(0, pitch)),
  ].join(",");
  return `https://api.mapbox.com/styles/v1/${match[1]}/${match[2]}/static/${camera}/${Math.max(1, Math.round(width * ratio))}x${Math.max(1, Math.round(height * ratio))}@2x?access_token=${encodeURIComponent(token)}`;
}

export function MapSnapshot({
  token,
  mapStyle,
  view,
  showMarker,
  markerOffsetY = 0,
  marker,
}: {
  token?: string;
  mapStyle: string;
  view: Partial<ViewState>;
  showMarker: boolean;
  markerOffsetY?: number;
  marker: React.ReactNode;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [result, setResult] = useState<{ url: string; state: "ready" | "error" }>();
  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    const measure = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  let url = "";
  let error = token ? "" : "Configure a public Mapbox token to export this map.";
  try {
    if (token && size.width && size.height) url = staticMapImageUrl(mapStyle, token, view, size.width, size.height);
  } catch {
    error = "This map cannot be rendered as an image.";
  }
  const state = error ? "error" : result?.url === url ? result.state : "loading";
  return (
    <div
      ref={root}
      data-map-snapshot=""
      data-map-state={state}
      data-map-error={
        error || (state === "error" ? "Mapbox could not load this map. Check the token and style access." : undefined)
      }
      style={{ position: "absolute", inset: 0 }}
    >
      {url && (
        <img
          key={url}
          src={url}
          alt="Location map"
          crossOrigin="anonymous"
          referrerPolicy="strict-origin"
          onLoad={() => setResult({ url, state: "ready" })}
          onError={() => setResult({ url, state: "error" })}
          style={{ width: "100%", height: "100%", display: "block" }}
        />
      )}
      {state === "error" && <p role="alert">{error || "Mapbox could not load this map."}</p>}
      {showMarker && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: `translate(-50%, calc(-100% + ${markerOffsetY}px))`,
          }}
        >
          {marker}
        </div>
      )}
    </div>
  );
}
